import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { listarSolicitudes } from '../../services/adminSolicitudes';
import { obtenerResumenAdmin, listarAdminAplicaciones } from '../../services/adminAplicaciones';
import { obtenerResumenFertilizantesAdmin, listarFertilizantesAdmin } from '../../services/adminFertilizantes';
import AppShell from '../../components/layout/AppShell';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return fechaaplicacion;
  }
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value ?? '—'}</p>
    </div>
  );
}

function AdminDashboardPage() {
  const { admin } = useAuth();
  const navigate = useNavigate();

  const [pendientesCount, setPendientesCount] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [ultimas, setUltimas] = useState([]);
  const [loadingResumen, setLoadingResumen] = useState(true);
  const [error, setError] = useState('');

  // Sección separada de Fertilizantes: métricas y últimas aplicaciones
  // propias, nunca mezcladas con las de Aplicaciones (fitosanitarias).
  const [resumenFert, setResumenFert] = useState(null);
  const [ultimasFert, setUltimasFert] = useState([]);
  const [loadingResumenFert, setLoadingResumenFert] = useState(true);
  const [errorFert, setErrorFert] = useState('');

  useEffect(() => {
    let active = true;

    listarSolicitudes('PENDIENTE').then((result) => {
      if (!active) return;
      if (result.ok) {
        setPendientesCount(result.solicitudes.length);
      }
    });

    obtenerResumenAdmin().then((result) => {
      if (!active) return;
      if (result.ok) {
        setResumen(result.resumen);
      } else {
        setError(result.message);
      }
    });

    listarAdminAplicaciones({ page: 1, pageSize: 5, estado: 'TODAS' }).then((result) => {
      if (!active) return;
      if (result.ok) {
        setUltimas(result.aplicaciones);
      }
      setLoadingResumen(false);
    });

    obtenerResumenFertilizantesAdmin().then((result) => {
      if (!active) return;
      if (result.ok) {
        setResumenFert(result.resumen);
      } else {
        setErrorFert(result.message);
      }
    });

    listarFertilizantesAdmin({ page: 1, pageSize: 5, estado: 'TODAS' }).then((result) => {
      if (!active) return;
      if (result.ok) {
        setUltimasFert(result.aplicaciones);
      }
      setLoadingResumenFert(false);
    });

    return () => {
      active = false;
    };
  }, []);

  return (
    <AppShell>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Administración</h1>
          <p className="mt-1 text-sm text-gray-500">Hola, {admin?.nombre}</p>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{error}</p>}

        <div>
          <p className="mb-3 text-sm font-medium text-gray-500">Resumen hoy</p>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <MetricCard label="Aplicaciones hoy" value={resumen?.hoy} />
            <MetricCard label="Últimos 7 días" value={resumen?.dias7} />
            <MetricCard label="Este mes" value={resumen?.mes} />
            <MetricCard label="Activas" value={resumen?.activas} />
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate('/admin/solicitudes')}
          className="rounded-2xl border border-gray-100 bg-white p-5 text-left shadow-sm transition-colors hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
        >
          <p className="text-sm font-medium text-gray-500">Solicitudes pendientes</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{pendientesCount ?? '—'}</p>

          <span className="mt-4 flex items-center gap-1 text-sm font-medium text-green-700">
            Revisar solicitudes
            <Icon name="chevronRight" className="h-4 w-4" />
          </span>
        </button>

        <div>
          <p className="mb-3 text-sm font-medium text-gray-500">Últimas aplicaciones</p>

          {loadingResumen ? (
            <p className="text-sm text-gray-500">Cargando...</p>
          ) : ultimas.length === 0 ? (
            <EmptyState message="Todavía no hay aplicaciones registradas." />
          ) : (
            <div className="flex flex-col gap-2">
              {ultimas.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => navigate(`/admin/aplicaciones/${a.id}`)}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">{formatFecha(a.fechaaplicacion)}</p>
                    <p className="truncate font-medium text-gray-900">
                      {a.productor_nombre || '—'} · {a.encargado_nombre || '—'}
                    </p>
                    <p className="truncate text-sm text-gray-500">
                      {a.huerto || '—'} · {a.cuartel || '—'}
                    </p>
                  </div>
                  <StatusBadge estado={a.estado} />
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate('/admin/aplicaciones')}
            className="mt-4 flex items-center gap-1 text-sm font-medium text-green-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          >
            Ver todas las aplicaciones
            <Icon name="chevronRight" className="h-4 w-4" />
          </button>
        </div>

        {/* Sección separada de Fertilizantes: métricas y últimas
            aplicaciones propias, nunca mezcladas con las de Aplicaciones. */}
        <div>
          <p className="mb-3 text-sm font-medium text-gray-500">Fertilizantes</p>

          {errorFert && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{errorFert}</p>}

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <MetricCard label="Hoy" value={resumenFert?.hoy} />
            <MetricCard label="Últimos 7 días" value={resumenFert?.dias7} />
            <MetricCard label="Este mes" value={resumenFert?.mes} />
            <MetricCard label="Activas" value={resumenFert?.activas} />
          </div>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-gray-500">Últimas aplicaciones de fertilizantes</p>

          {loadingResumenFert ? (
            <p className="text-sm text-gray-500">Cargando...</p>
          ) : ultimasFert.length === 0 ? (
            <EmptyState message="Todavía no hay aplicaciones de fertilizantes registradas." />
          ) : (
            <div className="flex flex-col gap-2">
              {ultimasFert.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => navigate(`/admin/fertilizantes/${a.id}`)}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">{formatFecha(a.fechaaplicacion)}</p>
                    <p className="truncate font-medium text-gray-900">
                      {a.productor_nombre || '—'} · {a.encargado_nombre || '—'}
                    </p>
                    <p className="truncate text-sm text-gray-500">
                      {a.huerto || '—'} · {a.cuartel || '—'}
                    </p>
                  </div>
                  <StatusBadge estado={a.estado} />
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate('/admin/fertilizantes')}
            className="mt-4 flex items-center gap-1 text-sm font-medium text-green-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          >
            Ver todas las aplicaciones de fertilizantes
            <Icon name="chevronRight" className="h-4 w-4" />
          </button>
        </div>
      </div>
    </AppShell>
  );
}

export default AdminDashboardPage;
